<script setup lang="ts">
import type { DealCard } from '#shared/types/deal'

const props = defineProps<{
  deal: DealCard
  imagePriority?: boolean
}>()

const config = useRuntimeConfig()
const { trackEvent } = useUmami()
const { user, isAdmin } = useAuth()
const router = useRouter()
const toast = useToast()
type SocialPublishChannel = 'telegram' | 'x' | 'all'

// ── Favorito ──────────────────────────────────────────────────────────────────
const isFavorited = ref(false)
const favoriteLoading = ref(false)
const socialPublishLoading = ref<SocialPublishChannel | null>(null)
const telegramPostedAt = ref(props.deal.telegramPostedAt)
const xPostedAt = ref(props.deal.xPostedAt)

watch(() => props.deal.telegramPostedAt, value => { telegramPostedAt.value = value })
watch(() => props.deal.xPostedAt, value => { xPostedAt.value = value })

async function toggleFavorite(e: MouseEvent) {
  e.preventDefault()
  e.stopPropagation()
  if (!user.value) {
    trackEvent('favorite-login-required', { dealId: props.deal.id, source: 'deal-card' })
    router.push(`/login?redirect=/oferta/${props.deal.slug}`)
    return
  }
  favoriteLoading.value = true
  try {
    if (isFavorited.value) {
      await $fetch(`/favorites/${props.deal.id}`, {
        baseURL: config.public.apiBaseUrl,
        method: 'DELETE',
        credentials: 'include' as RequestCredentials,
      })
      isFavorited.value = false
      trackEvent('favorite-remove', { dealId: props.deal.id, source: 'deal-card' })
    }
    else {
      await $fetch(`/favorites/${props.deal.id}`, {
        baseURL: config.public.apiBaseUrl,
        method: 'POST',
        credentials: 'include' as RequestCredentials,
      })
      isFavorited.value = true
      trackEvent('favorite-add', { dealId: props.deal.id, source: 'deal-card' })
    }
  }
  catch {
    // Falha nao bloqueante para acao de favorito no card.
  }
  finally { favoriteLoading.value = false }
}

const ctaUrl = computed(() => {
  if (props.deal.shortCode && config.public.shortLinkBase) {
    return `${config.public.shortLinkBase}/${props.deal.shortCode}`
  }
  return props.deal.url
})

function handleCtaClick() {
  trackEvent('deal-click', {
    dealId: props.deal.id,
    slug: props.deal.slug,
    store: props.deal.store.name,
    price: props.deal.price ?? 0,
  })
}

function copyCouponCode() {
  if (!props.deal.couponCode) return
  navigator.clipboard.writeText(props.deal.couponCode)
  trackEvent('coupon-copy', {
    dealId: props.deal.id,
    store: props.deal.store.name,
    source: 'deal-card',
  })
}

async function publishToSocials(e: MouseEvent, channel: SocialPublishChannel) {
  e.preventDefault()
  e.stopPropagation()

  if (!isAdmin.value || socialPublishLoading.value) return

  const label = channel === 'telegram' ? 'Telegram' : channel === 'x' ? 'X/Twitter' : 'Telegram e X/Twitter'
  socialPublishLoading.value = channel
  try {
    const updated = await $fetch<{
      socialPublishError?: string | null
      telegramPostedAt?: string | null
      xPostedAt?: string | null
    }>(`/admin/deals/${props.deal.id}/publish-socials`, {
      baseURL: config.public.apiBaseUrl,
      method: 'POST',
      body: { channel },
      credentials: 'include' as RequestCredentials,
    })

    telegramPostedAt.value = updated.telegramPostedAt ?? telegramPostedAt.value
    xPostedAt.value = updated.xPostedAt ?? xPostedAt.value

    trackEvent('admin-social-publish', {
      dealId: props.deal.id,
      source: 'deal-card',
      channel,
      telegram: Boolean(updated.telegramPostedAt),
      x: Boolean(updated.xPostedAt),
    })

    toast.add({
      title: updated.socialPublishError ? 'Publicação concluída com alerta' : `Promoção publicada em ${label}`,
      description: updated.socialPublishError ?? `${label} acionado para esta promoção.`,
      color: updated.socialPublishError ? 'warning' : 'success',
    })
  }
  catch (err: unknown) {
    const message = (err as { data?: { message?: string } })?.data?.message ?? `Erro ao publicar em ${label}`
    toast.add({ title: 'Erro ao publicar', description: message, color: 'error' })
  }
  finally {
    socialPublishLoading.value = null
  }
}

const isExpired = computed(() => {
  if (!props.deal.expiresAt) return false
  return new Date(props.deal.expiresAt) < new Date()
})

const timeAgo = computed(() => {
  const date = new Date(props.deal.publishedAt)
  const now = new Date()
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000 / 60)
  if (diff < 60) return `${diff}min`
  if (diff < 60 * 24) return `${Math.floor(diff / 60)}h`
  return `${Math.floor(diff / 60 / 24)}d`
})
</script>

<template>
  <article
    class="group flex h-full min-w-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white transition-shadow duration-200 hover:shadow-md dark:border-gray-800 dark:bg-gray-900"
    :class="{ 'opacity-60': isExpired }"
  >
    <div class="relative aspect-video bg-white overflow-hidden border-b border-gray-200 dark:border-gray-800">
        <NuxtLink :to="`/oferta/${deal.slug}`" class="absolute inset-0 block">
          <NuxtImg
            v-if="deal.thumbnailUrl || deal.imageUrl"
            :src="(deal.thumbnailUrl || deal.imageUrl)!"
            :alt="deal.title"
            class="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
            width="360"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw"
            format="webp"
            quality="72"
            :loading="imagePriority ? 'eager' : 'lazy'"
            :fetchpriority="imagePriority ? 'high' : 'auto'"
            :preload="imagePriority"
            decoding="async"
          />
          <div v-else class="w-full h-full flex items-center justify-center text-gray-300">
            <UIcon name="i-heroicons-photo" class="w-12 h-12" />
          </div>
        </NuxtLink>

        <!-- Desconto badge — mesmo estilo do botão favorito: fundo branco + ring -->
        <div v-if="deal.discount" class="absolute top-2 left-2 pointer-events-none">
          <span class="inline-flex items-center bg-white dark:bg-gray-700 shadow-md ring-1 ring-gray-200 dark:ring-gray-600 text-red-600 dark:text-white font-bold text-xs px-1.5 py-0.5 rounded-full">
            -{{ deal.discount }}%
          </span>
        </div>

        <div class="absolute bottom-2 left-2 pointer-events-none">
          <span class="inline-flex items-center rounded-full bg-slate-900/88 px-2 py-1 text-[11px] font-semibold text-white shadow-md ring-1 ring-white/10 backdrop-blur">
            {{ deal.category.name }}
          </span>
        </div>

        <!-- Botão favorito — ring sutil para destacar sobre fundo branco -->
        <button
          class="absolute top-2 right-2 w-7 h-7 rounded-full shadow-md ring-1 ring-gray-200 dark:ring-gray-600 transition-all flex items-center justify-center"
          :class="isFavorited
            ? 'bg-white dark:bg-gray-700 text-red-500'
            : 'bg-white dark:bg-gray-700 text-gray-400 hover:text-red-500'"
          :title="isFavorited ? 'Remover dos favoritos' : 'Salvar nos favoritos'"
          @click="toggleFavorite"
        >
          <UIcon
            v-if="!favoriteLoading"
            :name="isFavorited ? 'i-heroicons-heart-solid' : 'i-heroicons-heart'"
            class="w-3.5 h-3.5"
          />
          <UIcon v-else name="i-heroicons-arrow-path" class="w-3.5 h-3.5 animate-spin" />
        </button>

        <div v-if="isAdmin" class="absolute top-10 right-2 flex flex-col gap-1">
          <button
            class="w-7 h-7 rounded-full bg-white shadow-md ring-1 ring-gray-200 transition-all flex items-center justify-center disabled:opacity-80 dark:bg-gray-700 dark:ring-gray-600"
            :class="telegramPostedAt
              ? '!bg-emerald-500 text-white ring-emerald-300 hover:!bg-emerald-600 dark:!bg-emerald-500 dark:ring-emerald-700'
              : socialPublishLoading === 'telegram'
                ? 'text-amber-500 ring-amber-200 dark:ring-amber-700'
                : 'text-sky-500 hover:text-sky-600'"
            :title="telegramPostedAt ? 'Promoção já publicada no Telegram' : 'Publicar promoção no Telegram'"
            :disabled="Boolean(socialPublishLoading)"
            @click="publishToSocials($event, 'telegram')"
          >
            <UIcon
              v-if="socialPublishLoading !== 'telegram'"
              name="i-heroicons-paper-airplane"
              class="w-3.5 h-3.5"
            />
            <UIcon v-else name="i-heroicons-arrow-path" class="w-3.5 h-3.5 animate-spin" />
          </button>
          <button
            class="w-7 h-7 rounded-full bg-white shadow-md ring-1 ring-gray-200 transition-all flex items-center justify-center disabled:opacity-80 dark:bg-gray-700 dark:ring-gray-600"
            :class="xPostedAt
              ? '!bg-emerald-500 text-white ring-emerald-300 hover:!bg-emerald-600 dark:!bg-emerald-500 dark:ring-emerald-700'
              : socialPublishLoading === 'x'
                ? 'text-amber-500 ring-amber-200 dark:ring-amber-700'
                : 'text-sky-500 hover:text-sky-600'"
            :title="xPostedAt ? 'Promoção já publicada no X/Twitter' : 'Publicar promoção no X/Twitter'"
            :disabled="Boolean(socialPublishLoading)"
            @click="publishToSocials($event, 'x')"
          >
            <UIcon
              v-if="socialPublishLoading !== 'x'"
              name="i-simple-icons-twitter"
              class="w-3.5 h-3.5"
            />
            <UIcon v-else name="i-heroicons-arrow-path" class="w-3.5 h-3.5 animate-spin" />
          </button>
        </div>
    </div>

    <!-- Conteúdo clicável -->
    <NuxtLink :to="`/oferta/${deal.slug}`" class="flex-1 p-3 space-y-2 min-w-0 overflow-hidden block">
      <!-- Loja + tempo -->
      <div class="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span
          class="flex items-center gap-1 hover:text-primary-500 cursor-pointer"
          @click.prevent.stop="$router.push(`/loja/${deal.store.slug}`)"
        >
          <NuxtImg
            v-if="deal.store.logoUrl"
            :src="deal.store.logoUrl"
            :alt="deal.store.name"
            class="w-4 h-4 object-contain rounded"
            width="32"
            height="32"
            sizes="16px"
            format="webp"
            quality="70"
            loading="lazy"
            decoding="async"
          />
          <span>{{ deal.store.name }}</span>
        </span>
        <span>{{ timeAgo }}</span>
      </div>

      <div class="flex flex-wrap items-center gap-1.5">
        <UBadge
          v-if="deal.source === 'PRICE_MONITOR'"
          color="success"
          variant="soft"
          size="sm"
          title="Queda de preço confirmada pelo nosso histórico — não é desconto inflado"
        >
          📉 Desconto real
        </UBadge>
        <UBadge
          v-if="deal.freeShipping"
          color="success"
          variant="soft"
          size="sm"
        >
          Frete gratis
        </UBadge>
        <UBadge
          v-if="deal.couponCode"
          color="warning"
          variant="soft"
          size="sm"
        >
          Tem cupom
        </UBadge>
      </div>

      <!-- Título -->
      <h3 class="text-sm font-medium text-gray-900 dark:text-white line-clamp-2 break-words group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
        {{ deal.title }}
      </h3>

      <!-- Preço -->
      <PriceTag
        :price="deal.price"
        :original-price="deal.originalPrice"
        size="md"
      />

      <!-- Cupom -->
      <div v-if="deal.couponCode" class="flex items-center gap-1" @click.stop>
        <UBadge color="primary" variant="outline" size="sm" class="font-mono tracking-wide cursor-pointer" @click="copyCouponCode">
          {{ deal.couponCode }}
          <UIcon name="i-heroicons-clipboard" class="ml-1 w-3 h-3" />
        </UBadge>
      </div>

      <!-- Temperatura + stats -->
      <div class="flex items-center justify-between pt-1">
        <TemperatureBadge :temperature="deal.temperature" />
        <div class="flex items-center gap-2 text-xs text-gray-400">
          <span class="flex items-center gap-0.5">
            <UIcon name="i-heroicons-arrow-up-circle" class="w-3.5 h-3.5" />
            {{ deal._count.votes }}
          </span>
          <span class="flex items-center gap-0.5">
            <UIcon name="i-heroicons-chat-bubble-left" class="w-3.5 h-3.5" />
            {{ deal._count.comments }}
          </span>
        </div>
      </div>
    </NuxtLink>

    <!-- CTA externo -->
    <div class="border-t border-gray-200 p-3 dark:border-gray-800">
      <a
        :href="ctaUrl"
        target="_blank"
        rel="noopener noreferrer nofollow"
        @click="handleCtaClick"
      >
        <UButton
          color="primary"
          class="w-full"
          :disabled="isExpired"
          icon="i-heroicons-arrow-top-right-on-square"
          trailing
        >
          {{ isExpired ? 'Oferta Expirada' : 'Ver Oferta' }}
        </UButton>
      </a>
    </div>
  </article>
</template>
